import styles from './style.postcss';

import React, { Component } from 'react';
import { List } from 'immutable';
import { ProductionStatus, Config, Icon, Store } from 'omni-common-ui';
import ExpansionButton from 'components/ExpansionButton';
import { TableView, TableViewTr, MemberCardTd,
  ProgressCardTd, ProgressBarTd, NormalTd, HeaderTd } from 'components/TableView';
import { parseProductionStatus } from 'domain/ProductionStatus';
import { fetch, buildUrl } from 'domain/Api';
import { actions as groupActions } from 'containers/Group';
import PropTypes from 'prop-types';

const ALL_COLUMNS = new List([
  {
    key: 'name',
    text: 'Name',
    sortable: true,
    required: true,
    renderCell: (row) => (
      <MemberCardTd name={`${row.studentGeneralInfo.firstName} ${row.studentGeneralInfo.lastName}`}
          surname={row.studentGeneralInfo.localName}
          avatarUrl={row.studentGeneralInfo.avatarUrl}
          gender={row.studentGeneralInfo.gender} />
    ),
    cellClass: '',
  },
  {
    key: 'status',
    text: 'Status',
    sortable: false,
    required: true,
    renderCell: (row) => {
      const { isInProduction } = row;
      const status = Object.assign({ isInProduction }, parseProductionStatus(row.status));
      return (
        <NormalTd>
          <ProductionStatus status={row.status} highlighted={status.isInProduction} />
        </NormalTd>
      );
    },
    cellClass: '',
  },
  {
    key: 'averageTotalScore',
    text: <span>Average <wbr />Total Score</span>,
    sortable: true,
    required: false,
    renderCell: (row) => <ProgressCardTd percent={row.averageTotalScore} />,
    cellClass: '',
  },
  {
    key: 'averageOnlineHomeworkCompletion',
    text: <span>Ave. Online <wbr />Practice Completion</span>,
    sortable: true,
    required: false,
    renderCell: (row) => (
      <ProgressBarTd className={styles.PerformanceCard_mobileLastColomn}
          percent={row.averageOnlineHomeworkCompletion} />
    ),
    cellClass: styles.PerformanceCard_mobileLastColomn,
  },
  {
    key: 'averageOnlineHomeworkAccuracy',
    text: <span>Ave. Online <wbr />Practice Accuray</span>,
    sortable: true,
    required: false,
    renderCell: (row) => (
      <ProgressBarTd className={styles.PerformanceCard_mobileLastColomn}
          percent={row.averageOnlineHomeworkAccuracy} />
    ),
    cellClass: styles.PerformanceCard_mobileLastColomn,
  },
  {
    key: 'averageOnlineHomeworkBestAccuracy',
    text: <span>Ave. Online <wbr />Practice Accuray</span>,
    sortable: true,
    required: false,
    renderCell: (row) => (
      <ProgressBarTd className={styles.PerformanceCard_mobileLastColomn}
          percent={row.averageOnlineHomeworkBestAccuracy} />
    ),
    cellClass: styles.PerformanceCard_mobileLastColomn,
  },
  {
    key: 'averagePaperBasedHomeworkCompletion',
    text: <span>Ave. Paper-based <wbr />Practice Completion</span>,
    sortable: true,
    required: false,
    renderCell: (row) => (
      <ProgressBarTd className={styles.PerformanceCard_mobileLastColomn}
          percent={row.averagePaperBasedHomeworkCompletion} />
    ),
    cellClass: styles.PerformanceCard_mobileLastColomn,
  },
  {
    key: 'ach',
    text: '% Present ACH',
    sortable: true,
    required: false,
    renderCell: (row) => {
      const ACHPersent = row.ach.produced === 0 ? '0%' :
        `${parseInt((row.ach.present * 100) / row.ach.produced, 10)}%`;
      return (
        <NormalTd className={styles.PerformanceCard_presentColomn}>
          {`${ACHPersent} (${row.ach.present}/${row.ach.produced})`}
        </NormalTd>
      );
    },
    cellClass: styles.PerformanceCard_presentColomn,
  },
]);

const REQUIRED_COLUMNS = ALL_COLUMNS
  .filter((col) => col.required)
  .map((col) => col.key).toArray();

const OPTIONAL_COLUMNS = ALL_COLUMNS
  .filter((col) => ! col.required)
  .map((col) => col.key).toArray();

const DEFAULT_OPTIONAL_COLUMNS = [
  'averageTotalScore',
  'averageOnlineHomeworkCompletion',
  'averageOnlineHomeworkAccuracy',
  'averageOnlineHomeworkBestAccuracy',
  'averagePaperBasedHomeworkCompletion',
  'ach',
];

// HFv4 has its own viewer that takes plain query params.
const buildParentPageUrl = (groupId, sessionId, studentId) => {
  const params = new URLSearchParams({
    groupId: groupId || '',
    sessionId: sessionId || '',
    studentId: studentId || '',
  });
  return `${Config.get('parentPageViewer') || ''}?${params.toString()}`;
};

// Other course types go to the parent app, which needs a token exchanged at click time
// so it does not expire while the table is open.
const openParentPage = (studentId) => {
  fetch(buildUrl(`/students/${studentId}/study-report-token`))
    .then((response) => {
      const redirectUrl = `${Config.get('parentPageAuthUrl') || ''}` +
        `?idTokenV2=${response.idToken}&accessTokenV2=${response.accessToken}&origin=learning`;
      window.open(redirectUrl, '_blank', 'noreferrer');
    })
    .catch(() => {
      // do nothing.
    });
};

const buildParentPageColumn = (isHFV4Group) => ({
  key: 'parentPage',
  text: 'Parent Page',
  sortable: false,
  required: false,
  renderCell: (row, tableProps = {}) => {
    const studentId = row.studentGeneralInfo.id;
    const linkProps = isHFV4Group ?
    {
      href: buildParentPageUrl(tableProps.groupId, tableProps.sessionId, studentId),
      target: '_blank',
      rel: 'noopener noreferrer',
    } :
    { onClick: () => openParentPage(studentId) };

    return (
      <NormalTd>
        <a className={styles.PerformanceCard_parentPageLink} {...linkProps}>
          View Parent Page
          <Icon id="arrow-right" className={styles.PerformanceCard_parentPageArrow} />
        </a>
      </NormalTd>
    );
  },
  cellClass: '',
});

class AveragesOverviewTable extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isExpand: false,
      sortKey: 'averageTotalScore',
      sortType: 'Ascending',
      filterParams: {},
    };
    this._handleOnFilterChange = this._handleOnFilterChange.bind(this);
  }

  _handleOnFilterChange(filterParams) {
    this.setState({ filterParams });
  }

  getVisibleColumns() {
    const {
      groupId,
      visibleOptionalColumnsInAveragesOverview = DEFAULT_OPTIONAL_COLUMNS,
    } = this.props;

    const validOptionalColumns = visibleOptionalColumnsInAveragesOverview
      .filter((colKey) => OPTIONAL_COLUMNS.includes(colKey));

    const finalVisibleColumnKeys = [...REQUIRED_COLUMNS, ...validOptionalColumns];

    const visibleColumns = ALL_COLUMNS
      .filter((column) => finalVisibleColumnKeys.includes(column.key))
      .sort((a, b) => {
        const indexA = finalVisibleColumnKeys.indexOf(a.key);
        const indexB = finalVisibleColumnKeys.indexOf(b.key);
        return indexA - indexB;
      });

    const isHFV4Group = groupActions.isHFV4CourseTypeGroup(Store.get().getState(), groupId);
    return visibleColumns.push(buildParentPageColumn(isHFV4Group));
  }

  _renderTableRow(row) {
    const visibleColumns = this.getVisibleColumns();
    return (
      <TableViewTr key={`${row.studentGeneralInfo.id}-performance`}>
        {visibleColumns.map((column) => column.renderCell(row, this.props))}
      </TableViewTr>
    );
  }

  _renderExpansionButton() {
    const { groupPerformance } = this.props;
    if (groupPerformance.studentsPerformance.size <= 4) {
      return null;
    }
    return <ExpansionButton isExpand={this.state.isExpand}
        className={styles.PerformanceCard_expansion}
        onExpansionButtonClick={() => this.setState({ isExpand: ! this.state.isExpand })} />;
  }

  _renderTableHeaderRow() {
    const { sortKey, sortType } = this.state;
    const { columnLabelsInAveragesOverview = {} } = this.props;
    const visibleColumns = this.getVisibleColumns();

    return (
      <TableViewTr key="PerformanceCard-hrow2">
        {visibleColumns.map((header) => (
          <HeaderTd sortable={header.sortable}
              className={header.cellClass}
              sortType={sortKey === header.key ? sortType : ''}
              onSort={() => this._sort(header.key)}
              key={header.key}>
            {columnLabelsInAveragesOverview[header.key] || header.text}
          </HeaderTd>
        ))}
      </TableViewTr>
    );
  }

  _sort(sortKey) {
    let sortType = 'Ascending';
    if (sortKey === this.state.sortKey) {
      sortType = this.state.sortType === 'Ascending' ? 'Descending' : 'Ascending';
    }
    this.setState({ sortKey, sortType });
  }

  render() {
    const { groupPerformance } = this.props;
    const { startUnitNumber, endUnitNumber } = groupPerformance;
    const tableTitle = startUnitNumber === endUnitNumber ?
      `Performance (Unit ${startUnitNumber})` :
      `Performance (Units ${startUnitNumber}-${endUnitNumber})`;
    const viewCount = groupPerformance.studentsPerformance.size >= 4 ?
      4 :
      groupPerformance.studentsPerformance.size;

    const sortedStudentsPerformance = groupPerformance.studentsPerformance
      .sort((currentObject, nextObject) => {
        const { sortKey, sortType } = this.state;
        let current = currentObject.get(sortKey);
        let next = nextObject.get(sortKey);

        switch (sortKey) {
          case 'name':
            current = `${currentObject.studentGeneralInfo.firstName} ${currentObject.studentGeneralInfo.lastName}`.toLowerCase();
            next = `${nextObject.studentGeneralInfo.firstName} ${nextObject.studentGeneralInfo.lastName}`.toLowerCase();
            break;
          case 'ach':
            current = current.present / (current.produced + 1);
            next = next.present / (next.produced + 1);
            break;
          default:
            break;
        }

        if (sortType === 'Ascending') {
          current = current === null ? 101 : current;
          next = next === null ? 101 : next;
        } else if (sortType === 'Descending') {
          current = current === null ? - 101 : current;
          next = next === null ? - 101 : next;
        }

        if (currentObject.status === 'Swapped') return 1;
        if (nextObject.status === 'Swapped') return - 1;

        if (current > next) return sortType === 'Ascending' ? 1 : - 1;
        if (current < next) return sortType === 'Ascending' ? - 1 : 1;
        return 0;
      });

    const source = this.state.isExpand ?
      sortedStudentsPerformance :
      sortedStudentsPerformance.slice(0, viewCount);

    return (
      <TableView source={source}
          tableTitle={tableTitle}
          renderTableRow={(row) => this._renderTableRow(row)}
          noResultsString="No Results."
          query={this.state.filterParams}
          onFilterChange={this._handleOnFilterChange}
          customerFooter={this._renderExpansionButton()}
          customerHeader={this._renderTableHeaderRow()} />
    );
  }
}

AveragesOverviewTable.propTypes = {
  groupPerformance: PropTypes.object.isRequired,
  groupId: PropTypes.string,
  sessionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  visibleOptionalColumnsInAveragesOverview: PropTypes.arrayOf(PropTypes.oneOf(OPTIONAL_COLUMNS)),
  columnLabelsInAveragesOverview: PropTypes.shape({
    name: PropTypes.node,
    status: PropTypes.node,
    averageTotalScore: PropTypes.node,
    averageOnlineHomeworkCompletion: PropTypes.node,
    averagePaperBasedHomeworkCompletion: PropTypes.node,
    ach: PropTypes.node,
  }),
};

AveragesOverviewTable.defaultProps = {
  visibleOptionalColumns: DEFAULT_OPTIONAL_COLUMNS,
  columnLabels: {},
};

export default AveragesOverviewTable;



// WEBPACK FOOTER //
// ./containers/Group/Dashboard/PerformanceCard/AveragesOverviewTable/index.jsx